/**
 * O texto livre em `.docx`.
 *
 * Diferente do `.docx` do trabalho acadêmico (`documento/docx.ts`, que monta o
 * arquivo a partir do documento estruturado), aqui a origem é **HTML** — o que o
 * editor de texto produziu. Então o caminho é o inverso: percorrer o HTML e
 * traduzir cada bloco no objeto do Word.
 *
 * O que vira o quê:
 *
 * | No editor | No Word |
 * |---|---|
 * | Título / Subtítulo / Seção (`h1`–`h3`) | estilos de título 1 a 3 |
 * | Parágrafo | parágrafo, com o alinhamento que estiver no `style` |
 * | Lista com marcador / numerada | lista com marcador / numerada |
 * | Citação (`blockquote`) | parágrafo recuado, em itálico |
 * | Tabela | tabela de verdade, com cabeçalho em negrito |
 * | Negrito, itálico, sublinhado, riscado | as mesmas marcas, no trecho |
 * | Cor e tamanho do texto | cor e corpo do trecho |
 *
 * **Imagem não entra no `.docx`** nesta versão: ela sai no PDF (a impressão do
 * navegador desenha a imagem) e continua no texto. Fazer o download da imagem e
 * embutir no Word é possível, mas o arquivo passaria a depender de rede no momento
 * da exportação, e é melhor avisar do que entregar um `.docx` pela metade em
 * silêncio.
 */

import {
  AlignmentType,
  BorderStyle,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from 'docx';
import { FAMILIA_DA_FONTE, type FormatoDoTexto } from '@miolos/core';

/** O que o Word recebe de cada trecho. */
interface MarcaDoTrecho {
  negrito?: boolean;
  italico?: boolean;
  sublinhado?: boolean;
  riscado?: boolean;
  cor?: string;
  /** Corpo em meia-ponto (Word conta assim: 28 = 14 pt). */
  tamanho?: number;
  link?: string;
}

const COR_DO_LINK = '1155CC';

/** O alinhamento que o editor gravou no `style` do bloco. */
function alinhamento(no: HTMLElement) {
  const estilo = (no.getAttribute('style') ?? '').toLowerCase();
  if (estilo.includes('text-align: center') || estilo.includes('text-align:center')) return AlignmentType.CENTER;
  if (estilo.includes('text-align: right') || estilo.includes('text-align:right')) return AlignmentType.RIGHT;
  if (estilo.includes('text-align: justify') || estilo.includes('text-align:justify')) return AlignmentType.JUSTIFIED;
  return AlignmentType.LEFT;
}

/** `14pt` → 28 (meio-pontos); `20px` → 30. */
function tamanhoEmMeioPontos(valor: string): number | undefined {
  const numero = Number.parseFloat(valor);
  if (!Number.isFinite(numero) || numero <= 0) return undefined;
  const pontos = /px/i.test(valor) ? numero * 0.75 : numero;
  return Math.round(pontos * 2);
}

/** `#ff0000` e `rgb(255, 0, 0)` viram `FF0000`, que é como o Word quer. */
function corDoWord(valor: string): string | undefined {
  const limpo = valor.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(limpo)) return limpo.slice(1).toUpperCase();
  if (/^#[0-9a-f]{3}$/.test(limpo)) return limpo.slice(1).split('').map((c) => c + c).join('').toUpperCase();
  const rgb = limpo.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (rgb) {
    const hex = rgb.slice(1, 4).map((parte) => Number(parte).toString(16).padStart(2, '0')).join('');
    return hex.toUpperCase();
  }
  return undefined;
}

/** Lê o `style` de um trecho (`color`, `font-size`) e devolve o que o Word entende. */
function estiloDoTrecho(no: HTMLElement): Partial<MarcaDoTrecho> {
  const estilo = no.getAttribute('style') ?? '';
  if (!estilo) return {};
  const marca: Partial<MarcaDoTrecho> = {};

  const cor = estilo.match(/(?:^|;)\s*color\s*:\s*([^;]+)/i);
  if (cor?.[1]) {
    const hex = corDoWord(cor[1]);
    if (hex) marca.cor = hex;
  }

  const tamanho = estilo.match(/font-size\s*:\s*([^;]+)/i);
  if (tamanho?.[1]) {
    const meioPontos = tamanhoEmMeioPontos(tamanho[1]);
    if (meioPontos) marca.tamanho = meioPontos;
  }

  return marca;
}

/** Os trechos de texto de um nó, com as marcas que ele herdou dos pais. */
function runsDoNo(no: Node, marca: MarcaDoTrecho = {}): TextRun[] {
  if (no.nodeType === Node.TEXT_NODE) {
    const texto = (no.textContent ?? '').replace(/\u00a0/g, ' ');
    if (!texto.trim() && !texto.includes(' ')) return [];
    return [
      new TextRun({
        text: texto,
        bold: marca.negrito,
        italics: marca.italico,
        underline: marca.sublinhado ? {} : undefined,
        strike: marca.riscado,
        color: marca.cor,
        size: marca.tamanho,
      }),
    ];
  }

  if (no.nodeType !== Node.ELEMENT_NODE) return [];
  const elemento = no as HTMLElement;
  const tag = elemento.tagName.toLowerCase();
  if (tag === 'br') return [new TextRun({ text: '', break: 1 })];

  const proxima: MarcaDoTrecho = { ...marca, ...estiloDoTrecho(elemento) };
  if (tag === 'strong' || tag === 'b') proxima.negrito = true;
  if (tag === 'em' || tag === 'i') proxima.italico = true;
  if (tag === 'u') proxima.sublinhado = true;
  if (tag === 's' || tag === 'strike' || tag === 'del') proxima.riscado = true;
  if (tag === 'a') {
    proxima.sublinhado = true;
    proxima.cor = proxima.cor ?? COR_DO_LINK;
    proxima.link = elemento.getAttribute('href') ?? undefined;
  }

  const trechos: TextRun[] = [];
  for (const filho of Array.from(elemento.childNodes)) trechos.push(...runsDoNo(filho, proxima));

  /*
   * O endereço do link vai junto do texto: o Word só mostra o link se o arquivo
   * tiver o campo de hyperlink montado, e um `.docx` com texto azul que não leva
   * a lugar nenhum é pior do que mostrar o endereço.
   */
  if (tag === 'a' && proxima.link && !proxima.link.startsWith('#')) {
    trechos.push(new TextRun({ text: ` (${proxima.link})`, size: proxima.tamanho, color: COR_DO_LINK }));
  }

  return trechos;
}

const NIVEL_DE_TITULO: Record<string, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  h1: HeadingLevel.HEADING_1,
  h2: HeadingLevel.HEADING_2,
  h3: HeadingLevel.HEADING_3,
  h4: HeadingLevel.HEADING_4,
  h5: HeadingLevel.HEADING_5,
  h6: HeadingLevel.HEADING_6,
};

/** Uma tabela do HTML vira tabela do Word. */
function tabelaEmDocx(tabela: HTMLTableElement): Table {
  const linhas = Array.from(tabela.querySelectorAll('tr'));
  const borda = { style: BorderStyle.SINGLE, size: 4, color: 'BBBBBB' };

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: linhas.map((linha) => {
      const celulas = Array.from(linha.querySelectorAll('th,td'));
      const cabecalho = Boolean(linha.querySelector('th'));
      return new TableRow({
        tableHeader: cabecalho,
        children: celulas.map(
          (celula) =>
            new TableCell({
              borders: { top: borda, bottom: borda, left: borda, right: borda },
              children: [
                new Paragraph({
                  children: runsDoNo(celula, cabecalho ? { negrito: true } : {}),
                }),
              ],
            }),
        ),
      });
    }),
  });
}

/** Os blocos de uma lista (`ul`/`ol`), um parágrafo por item. */
function listaEmDocx(lista: HTMLElement, numerada: boolean): Paragraph[] {
  return Array.from(lista.children)
    .filter((filho) => filho.tagName.toLowerCase() === 'li')
    .map(
      (item) =>
        new Paragraph({
          children: runsDoNo(item),
          alignment: alinhamento(item as HTMLElement),
          bullet: numerada ? undefined : { level: 0 },
          numbering: numerada ? { reference: 'lista-numerada', level: 0 } : undefined,
          spacing: { after: 80 },
        }),
    );
}

/** Percorre o HTML e devolve os blocos do Word, na ordem do papel. */
function filhosDoHtml(raiz: HTMLElement): (Paragraph | Table)[] {
  const filhos: (Paragraph | Table)[] = [];

  for (const no of Array.from(raiz.children)) {
    const elemento = no as HTMLElement;
    const tag = elemento.tagName.toLowerCase();

    if (tag === 'table') {
      filhos.push(tabelaEmDocx(elemento as HTMLTableElement));
      continue;
    }
    if (tag === 'ul' || tag === 'ol') {
      filhos.push(...listaEmDocx(elemento, tag === 'ol'));
      continue;
    }
    if (tag === 'div' || tag === 'section' || tag === 'article') {
      filhos.push(...filhosDoHtml(elemento));
      continue;
    }
    if (tag === 'hr') {
      filhos.push(
        new Paragraph({
          text: '',
          border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CCCCCC', space: 1 } },
        }),
      );
      continue;
    }
    if (tag === 'blockquote') {
      filhos.push(
        new Paragraph({
          children: runsDoNo(elemento, { italico: true }),
          alignment: alinhamento(elemento),
          indent: { left: 720 },
          spacing: { after: 120 },
        }),
      );
      continue;
    }

    const titulo = NIVEL_DE_TITULO[tag];
    if (titulo) {
      filhos.push(
        new Paragraph({ heading: titulo, alignment: alinhamento(elemento), children: runsDoNo(elemento) }),
      );
      continue;
    }

    if (tag === 'p' || tag === 'pre') {
      const runs = runsDoNo(elemento);
      filhos.push(
        new Paragraph({
          children: runs.length > 0 ? runs : [new TextRun({ text: '' })],
          alignment: alinhamento(elemento),
          spacing: { after: 160 },
        }),
      );
      continue;
    }

    /* Qualquer outra coisa (span solto, imagem): vira parágrafo com o texto dela. */
    const runs = runsDoNo(elemento);
    if (runs.length > 0) filhos.push(new Paragraph({ children: runs, spacing: { after: 160 } }));
  }

  return filhos;
}

/** O nome do arquivo, sem acento e sem barra — o que o Windows aceita. */
export function nomeDoArquivoDoTexto(titulo: string): string {
  const limpo = titulo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return `${limpo || 'texto'}.docx`;
}

/**
 * Monta e baixa o `.docx` do texto livre.
 *
 * A folha sai em A4 com 2,5 cm de margem, na fonte que a pessoa escolheu para
 * ler — o `.docx` é para continuar editando, então a fonte é a da tela, e não uma
 * fonte de escritório qualquer.
 */
export async function baixarTextoDocx(html: string, titulo: string, formato: FormatoDoTexto): Promise<void> {
  const documento = new DOMParser().parseFromString(`<div id="raiz">${html}</div>`, 'text/html');
  const raiz = documento.getElementById('raiz');
  if (!raiz) throw new Error('Não foi possível ler o texto para exportar.');

  const familia = FAMILIA_DA_FONTE[formato.fonte];
  const documentoWord = new Document({
    creator: 'Miolix',
    title: titulo,
    description: 'Texto exportado do editor Miolix',
    numbering: {
      config: [
        {
          reference: 'lista-numerada',
          levels: [{ level: 0, format: 'decimal', text: '%1.', alignment: AlignmentType.LEFT }],
        },
      ],
    },
    styles: {
      default: {
        document: {
          run: { font: familia, size: formato.corpoPt * 2 },
          paragraph: { spacing: { line: 300 } },
        },
      },
    },
    sections: [
      {
        properties: {
          page: { margin: { top: 1417, right: 1417, bottom: 1417, left: 1417 } },
        },
        children: [
          new Paragraph({
            children: [new TextRun({ text: titulo, bold: true, size: formato.corpoPt * 2 + 8 })],
            spacing: { after: 320 },
          }),
          ...filhosDoHtml(raiz),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(documentoWord);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeDoArquivoDoTexto(titulo);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
